import React, { useState, useEffect, useCallback } from 'react';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import {
  CheckCircle, XCircle, Clock, AlertCircle, ChevronLeft, ChevronRight,
  Package, Navigation, User, Mail, ShieldCheck,
} from 'lucide-react';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ROLE_INFO = {
  ROLE_CARRIER: { label: 'Transporteur', icon: <Navigation size={13} />, color: '#C8832A', bg: '#fef9f0' },
  ROLE_SENDER:  { label: 'Expéditeur',   icon: <Package size={13} />,    color: '#0e7a68', bg: '#ecfdf5' },
};

const STATUS_TABS = [
  { key: 'PENDING',  label: 'En attente', icon: <Clock size={14} /> },
  { key: 'APPROVED', label: 'Approuvés',  icon: <CheckCircle size={14} /> },
  { key: 'REJECTED', label: 'Refusés',    icon: <XCircle size={14} /> },
  { key: 'all',      label: 'Tous',       icon: null },
];

const STATUS_COLORS = {
  PENDING:  { color: '#92400e', bg: '#fef3c7' },
  APPROVED: { color: '#065f46', bg: '#d1fae5' },
  REJECTED: { color: '#991b1b', bg: '#fee2e2' },
};

function RejectModal({ req, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const info = ROLE_INFO[req?.requested_role];

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) return;
    setLoading(true);
    await onConfirm(req.id, reason.trim());
    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28,
        width: '100%', maxWidth: 460,
        boxShadow: '0 20px 60px rgba(0,0,0,.2)',
      }}>
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6, color: '#111827' }}>
          Refuser la demande
        </div>
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>
          Indiquez un motif clair — l'utilisateur le recevra en notification.
        </div>
        {req && (
          <div style={{
            background: '#f9fafb', borderRadius: 8, padding: '10px 14px',
            marginBottom: 16, fontSize: 13, color: '#374151',
            display: 'flex', flexDirection: 'column', gap: 3,
          }}>
            <div style={{ fontWeight: 600 }}>{req.user?.first_name} {req.user?.last_name}</div>
            <div style={{ color: '#6B7280' }}>{req.user?.email}</div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 4,
              fontSize: 12, color: info?.color, background: info?.bg,
              padding: '3px 10px', borderRadius: 20, fontWeight: 600, width: 'fit-content',
            }}>
              {info?.icon} Rôle {info?.label}
            </div>
          </div>
        )}
        <form onSubmit={submit}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
            Motif du refus *
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Ex: Profil insuffisamment détaillé, manque de justificatif..."
            rows={4}
            required
            style={{
              width: '100%', borderRadius: 8, border: '1.5px solid #D1D5DB',
              padding: '10px 12px', fontSize: 13.5, resize: 'vertical',
              fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1, border: '1.5px solid #E5E7EB', background: '#fff',
                borderRadius: 8, padding: 10, fontSize: 13.5, fontWeight: 600,
                cursor: 'pointer', color: '#374151',
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || reason.trim().length < 5}
              style={{
                flex: 2, background: '#dc2626', color: '#fff',
                border: 'none', borderRadius: 8, padding: 10,
                fontSize: 13.5, fontWeight: 600,
                cursor: loading ? 'wait' : 'pointer',
                opacity: reason.trim().length < 5 ? 0.6 : 1,
              }}
            >
              {loading ? 'Envoi...' : 'Confirmer le refus'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminRoleRequests() {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [requests, setRequests] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, page, limit: 20 });
      const { data } = await axios.get(`${API}/admin/role-requests?${params}`);
      setRequests(data.data || []);
      setMeta(data.meta || { total: 0, page, limit: 20 });
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => { load(1); }, [load]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await axios.patch(`${API}/admin/role-requests/${id}/approve`);
      toast.success('Demande approuvée. L\'utilisateur a été notifié.');
      load(meta.page);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(id);
    try {
      await axios.patch(`${API}/admin/role-requests/${id}/reject`, { reason });
      toast.success('Demande refusée. L\'utilisateur a été notifié.');
      setRejectTarget(null);
      load(meta.page);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(meta.total / meta.limit);

  return (
    <div>
      <div className="mlc-topbar" style={{ marginBottom: 20 }}>
        <div>
          <div className="c2s-topbar-title">Demandes de rôles</div>
          <div className="c2s-topbar-sub">Modérez les demandes de rôles des utilisateurs</div>
        </div>
        <div style={{ fontSize: 13, color: '#6B7280', fontWeight: 600 }}>
          {meta.total} demande{meta.total !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Filtres statut */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {STATUS_TABS.map(tab => {
          const active = statusFilter === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                border: active ? '1.5px solid #0e7a68' : '1.5px solid #E5E7EB',
                background: active ? '#ecfdf5' : '#fff',
                color: active ? '#0e7a68' : '#6B7280',
                cursor: 'pointer',
              }}
            >
              {tab.icon} {tab.label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', fontSize: 14 }}>
          Chargement...
        </div>
      ) : requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', fontSize: 14 }}>
          Aucune demande dans cette catégorie.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {requests.map(req => {
            const info = ROLE_INFO[req.requested_role];
            const sc = STATUS_COLORS[req.status] || {};
            const isLoading = actionLoading === req.id;

            return (
              <div key={req.id} style={{
                border: '1px solid #E5E7EB', borderRadius: 12,
                background: '#fff', padding: '16px 18px',
                display: 'flex', alignItems: 'flex-start', gap: 14,
              }}>
                {/* Avatar */}
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: '#f3f4f6', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <User size={18} color="#9CA3AF" />
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                      {req.user?.first_name} {req.user?.last_name}
                    </span>
                    {req.user?.is_verified && (
                      <span title="Vérifié" style={{ color: '#0e7a68' }}>
                        <ShieldCheck size={14} />
                      </span>
                    )}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      color: sc.color, background: sc.bg,
                    }}>
                      {req.status}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
                    <Mail size={11} /> {req.user?.email}
                  </div>

                  {/* Rôle demandé + type profil + rôles actuels */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      color: info?.color, background: info?.bg,
                      border: `1px solid ${info?.color}30`,
                    }}>
                      {info?.icon} Demande : {info?.label}
                    </span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      color: req.profile_type === 'PROFESSIONAL' ? '#1d4ed8' : '#6B7280',
                      background: req.profile_type === 'PROFESSIONAL' ? '#eff6ff' : '#f3f4f6',
                    }}>
                      {req.profile_type === 'PROFESSIONAL' ? '🏢 Entreprise' : '👤 Particulier'}
                    </span>
                    {req.is_rerequest && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                        color: '#991b1b', background: '#fef2f2', border: '1px solid #fca5a5',
                      }}>
                        <AlertCircle size={11} />
                        Re-demande #{(req.previous_renounce_count || 0) + 1} — vérifier l'historique
                      </span>
                    )}
                    {req.user?.roles?.map(r => {
                      const ri = ROLE_INFO[r];
                      return ri ? (
                        <span key={r} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 20, fontSize: 11,
                          color: '#6B7280', background: '#f3f4f6',
                        }}>
                          {ri.icon} {ri.label}
                        </span>
                      ) : null;
                    })}
                  </div>

                  {/* Raison */}
                  <div style={{
                    fontSize: 12.5, color: '#374151', background: '#f9fafb',
                    padding: '8px 12px', borderRadius: 8, borderLeft: '3px solid #E5E7EB',
                    lineHeight: 1.5,
                  }}>
                    {req.reason}
                  </div>

                  {req.reject_reason && (
                    <div style={{
                      marginTop: 8, fontSize: 12, color: '#991b1b',
                      background: '#fee2e2', padding: '6px 10px', borderRadius: 6,
                      display: 'flex', alignItems: 'flex-start', gap: 6,
                    }}>
                      <AlertCircle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                      Refusé : {req.reject_reason}
                    </div>
                  )}

                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8 }}>
                    {new Date(req.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </div>
                </div>

                {/* Actions */}
                {req.status === 'PENDING' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => setApproveTarget(req)}
                      disabled={isLoading}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: '#0e7a68', color: '#fff', border: 'none',
                        borderRadius: 8, padding: '8px 14px', fontSize: 12.5,
                        fontWeight: 600, cursor: isLoading ? 'wait' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                      }}
                    >
                      <CheckCircle size={13} />
                      {isLoading ? '...' : 'Approuver'}
                    </button>
                    <button
                      onClick={() => setRejectTarget(req)}
                      disabled={isLoading}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        background: '#fff', color: '#dc2626',
                        border: '1.5px solid #fca5a5',
                        borderRadius: 8, padding: '8px 14px', fontSize: 12.5,
                        fontWeight: 600, cursor: isLoading ? 'wait' : 'pointer',
                      }}
                    >
                      <XCircle size={13} />
                      Refuser
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 24 }}>
          <button
            onClick={() => load(meta.page - 1)}
            disabled={meta.page <= 1 || loading}
            style={{
              border: '1.5px solid #E5E7EB', background: '#fff',
              borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', color: '#374151',
              opacity: meta.page <= 1 ? 0.4 : 1,
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 13, color: '#6B7280' }}>
            Page {meta.page} / {totalPages}
          </span>
          <button
            onClick={() => load(meta.page + 1)}
            disabled={meta.page >= totalPages || loading}
            style={{
              border: '1.5px solid #E5E7EB', background: '#fff',
              borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', color: '#374151',
              opacity: meta.page >= totalPages ? 0.4 : 1,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Modal de refus */}
      {rejectTarget && (
        <RejectModal
          req={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onConfirm={handleReject}
        />
      )}

      <ConfirmDialog
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        onConfirm={() => { const r = approveTarget; setApproveTarget(null); handleApprove(r.id); }}
        variant="financial"
        icon="check"
        title="Approuver la demande ?"
        subtitle={approveTarget ? `${approveTarget.user?.first_name || ''} ${approveTarget.user?.last_name || ''} — ${ROLE_INFO[approveTarget.requested_role]?.label || approveTarget.requested_role}`.trim() : undefined}
        confirmLabel="Confirmer l'approbation"
        busy={!!actionLoading}
      />
    </div>
  );
}
